import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  ShoppingCart,
  User,
  Calendar,
  Euro,
  Split,
  LayoutList,
  GanttChartSquare,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  Clock,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { PageTransition } from '../components/PageTransition';
import { BottomSheet } from '../components/shared/bottom-sheet';
import { useMobile } from '../hooks/use-mobile';
import { useNotification } from '../contexts/NotificationContext';
import {
  orderAssignmentService,
  OrderAssignment,
  CreateOrderAssignment,
} from '../services/orderAssignmentService';
import { userService } from '../services/userService';
import { orderService } from '../services/orderService';
import type { OrderDeleteImpact } from '../services/orderService';
import type { OrderWorkedHoursByUser } from '../services/orderService';
import type { OrderWorkedHoursByAssignment } from '../services/orderService';
import { clientSupplierService } from '../services/clientSupplierService';
import { timesheetService } from '../services/timesheetService';
import { getRoleLabel } from '../lib/role-labels';
import { ClientType } from '@timesheet/schemas';

interface OrderSummary {
  totalHours: number;
  netProfit: number | null;
  billingRevenue: number | null;
  activeUsers: number;
}

interface UserOption {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface OrderOption {
  id: number;
  code: string;
  description: string;
  clientId: number;
  supplierId?: number | null;
  plafond?: number | null;
  startDate?: string;
  endDate?: string;
  client?: { id: number; name: string };
}

interface OrderEditFormData {
  code: string;
  description: string;
  startDate: string;
  endDate: string;
  plafond: string;
  clientId: number;
  supplierId: number | null;
}

interface SplitFormData {
  splitDate: string;
  costRate: string;
  sellRate: string;
}

const initialFormData: CreateOrderAssignment = {
  userId: 0,
  orderId: 0,
  startDate: format(new Date(), 'yyyy-MM-dd'),
  endDate: format(new Date(new Date().getFullYear(), 11, 31), 'yyyy-MM-dd'),
  costRate: undefined,
  sellRate: undefined,
};

const initialOrderEditData: OrderEditFormData = {
  code: '',
  description: '',
  startDate: format(new Date(), 'yyyy-MM-dd'),
  endDate: format(new Date(), 'yyyy-MM-dd'),
  plafond: '',
  clientId: 0,
  supplierId: null,
};

const formatPlafondForUi = (value: number): string => new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(value);

const parseItalianPlafond = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const compact = trimmed.replace(/\s/g, '');
  const italianDecimalPattern = /^(\d+|\d{1,3}(\.\d{3})+)(,\d{1,2})?$/;
  if (!italianDecimalPattern.test(compact)) {
    throw new Error('invalid-plafond-format');
  }

  const normalized = compact.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error('invalid-plafond-format');
  }

  return parsed;
};

// Helper to reduce month labels only on mobile; desktop keeps all month ticks visible
const getFilteredMonthTicks = (monthTicks: Date[], isMobile: boolean): Set<string> => {
  if (!isMobile) {
    return new Set(monthTicks.map((d) => `${d.getFullYear()}-${d.getMonth()}`));
  }

  if (monthTicks.length <= 3) {
    return new Set(monthTicks.map((d) => `${d.getFullYear()}-${d.getMonth()}`));
  }

  const visibleIndices = new Set<number>();
  visibleIndices.add(0); // First
  visibleIndices.add(Math.floor(monthTicks.length / 2)); // Middle
  visibleIndices.add(monthTicks.length - 1); // Last

  return new Set(
    Array.from(visibleIndices).map((idx) => {
      const d = monthTicks[idx];
      return `${d.getFullYear()}-${d.getMonth()}`;
    })
  );
};

const OrderAssignmentManager: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useMobile();
  const { showNotification } = useNotification();

  // Data state
  const [assignments, setAssignments] = useState<OrderAssignment[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [orders, setOrders] = useState<OrderOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orderSummary, setOrderSummary] = useState<OrderSummary | null>(null);
  const [workedHoursByUser, setWorkedHoursByUser] = useState<Record<number, number>>({});
  const [workedHoursByAssignment, setWorkedHoursByAssignment] = useState<Record<number, number>>({});

  const getErrorMessage = useCallback((error: unknown, fallback: string) => {
    if (!axios.isAxiosError(error)) return fallback;

    const message = error.response?.data?.message;
    if (Array.isArray(message)) {
      return message[0] || fallback;
    }

    return typeof message === 'string' ? message : fallback;
  }, []);

  // UI state
  const [selectedOrderId, setSelectedOrderId] = useState<string>(() => searchParams.get('orderId') ?? '');
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<CreateOrderAssignment>(initialFormData);
  const [editingAssignment, setEditingAssignment] = useState<OrderAssignment | null>(null);
  const [orderEditOpen, setOrderEditOpen] = useState(false);
  const [savingOrderEdit, setSavingOrderEdit] = useState(false);
  const [orderEditData, setOrderEditData] = useState<OrderEditFormData>(initialOrderEditData);
  const [viewMode, setViewMode] = useState<'table' | 'gantt'>(() => {
    return (localStorage.getItem('orderAssignments.viewMode') as 'table' | 'gantt') ?? 'table';
  });
  const [hideClosedGroups, setHideClosedGroups] = useState(false);

  const toggleViewMode = () => {
    setViewMode((prev) => {
      const next = prev === 'table' ? 'gantt' : 'table';
      localStorage.setItem('orderAssignments.viewMode', next);
      return next;
    });
  };

  const [splitOpen, setSplitOpen] = useState(false);
  const [splitTarget, setSplitTarget] = useState<OrderAssignment | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deletingAssignmentId, setDeletingAssignmentId] = useState<number | null>(null);
  const [deleteOrderOpen, setDeleteOrderOpen] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState<OrderDeleteImpact | null>(null);
  const [loadingDeleteImpact, setLoadingDeleteImpact] = useState(false);
  const [hasApprovedTimesheetsForSelectedOrder, setHasApprovedTimesheetsForSelectedOrder] = useState(false);
  const [loadingDeleteEligibility, setLoadingDeleteEligibility] = useState(false);
  const [clients, setClients] = useState<Array<{ id: number; name: string }>>([]);
  const [suppliers, setSuppliers] = useState<Array<{ id: number; name: string }>>([]);
  const [splitForm, setSplitForm] = useState<SplitFormData>({
    splitDate: format(new Date(), 'yyyy-MM-dd'),
    costRate: '',
    sellRate: '',
  });

  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const toggleGroup = (key: number) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const [expandedUserGroups, setExpandedUserGroups] = useState<Set<number>>(new Set());
  const toggleUserGroup = (key: number) =>
    setExpandedUserGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, ordersData, clientSuppliers] = await Promise.all([
        userService.getAll(),
        orderService.getAll(),
        clientSupplierService.getAll(),
      ]);
      setUsers(usersData);
      setOrders(ordersData);
      setClients(
        clientSuppliers
          .filter((cs) => (cs.type === ClientType.CLIENT || cs.type === ClientType.BOTH) && cs.isActive !== false)
          .map((cs) => ({ id: cs.id, name: cs.name })),
      );
      setSuppliers(
        clientSuppliers
          .filter((cs) => (cs.type === ClientType.SUPPLIER || cs.type === ClientType.BOTH) && cs.isActive !== false)
          .map((cs) => ({ id: cs.id, name: cs.name })),
      );

      if (ordersData.length > 0 && !selectedOrderId && !searchParams.get('orderId')) {
        setSelectedOrderId(String(ordersData[0].id));
      }
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedOrderId]);

  useEffect(() => {
    const requestedOrderId = searchParams.get('orderId');

    if (requestedOrderId) {
      setSelectedOrderId(requestedOrderId);
    }
  }, [searchParams]);

  const fetchOrderAssignments = useCallback(async (orderId: number) => {
    try {
      const data = await orderAssignmentService.getByOrder(orderId);
      setAssignments(data);
    } catch (error) {
      console.error('Failed to fetch order assignments:', error);
    }
  }, []);

  const fetchOrderSummary = useCallback(async (order: OrderOption) => {
    if (!order.startDate || !order.endDate) return;
    try {
      const [stats, hoursByUser, hoursByAssignment] = await Promise.all([
        timesheetService.getDashboardStats(order.startDate, order.endDate),
        orderService.getWorkedHoursByUser(order.id),
        orderService.getWorkedHoursByAssignment(order.id),
      ]);
      const orderStat = stats.orderStats?.find((s: { orderId: number }) => s.orderId === order.id);
      const today2 = new Date();
      const activeUserIds = new Set(
        (await orderAssignmentService.getByOrder(order.id))
          .filter((a) => {
            const start = new Date(a.startDate);
            const end = a.endDate ? new Date(a.endDate) : new Date(2099, 11, 31);
            return start <= today2 && today2 <= end;
          })
          .map((a) => a.userId)
      );
      setOrderSummary({
        totalHours: orderStat?.totalHours ?? 0,
        netProfit: orderStat?.netProfit ?? null,
        billingRevenue: orderStat?.billingRate != null && orderStat?.totalHours != null
          ? Number(orderStat.billingRate) * Number(orderStat.totalHours)
          : null,
        activeUsers: activeUserIds.size,
      });

      setWorkedHoursByUser(
        hoursByUser.reduce<Record<number, number>>((acc, entry: OrderWorkedHoursByUser) => {
          acc[entry.userId] = entry.hours;
          return acc;
        }, {}),
      );

      setWorkedHoursByAssignment(
        hoursByAssignment.reduce<Record<number, number>>((acc, entry: OrderWorkedHoursByAssignment) => {
          acc[entry.assignmentId] = entry.hours;
          return acc;
        }, {}),
      );
    } catch {
      setOrderSummary(null);
      setWorkedHoursByUser({});
      setWorkedHoursByAssignment({});
    }
  }, []);

  const formatWorkedHoursForUser = (userId: number): string => {
    const hours = workedHoursByUser[userId] ?? 0;
    return `${hours}h`;
  };

  const formatWorkedHoursForAssignment = (assignmentId: number): string => {
    const hours = workedHoursByAssignment[assignmentId] ?? 0;
    return `${hours}h`;
  };

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (selectedOrderId) {
      fetchOrderAssignments(parseInt(selectedOrderId));
    }
  }, [selectedOrderId, fetchOrderAssignments]);

  const selectedOrder = orders.find((o) => String(o.id) === selectedOrderId);

  useEffect(() => {
    if (selectedOrder) {
      fetchOrderSummary(selectedOrder);
    } else {
      setOrderSummary(null);
    }
  }, [selectedOrder, fetchOrderSummary]);

  useEffect(() => {
    let isMounted = true;

    const loadDeleteEligibility = async () => {
      if (!selectedOrder) {
        if (isMounted) {
          setHasApprovedTimesheetsForSelectedOrder(false);
          setLoadingDeleteEligibility(false);
        }
        return;
      }

      if (isMounted) {
        setLoadingDeleteEligibility(true);
      }

      try {
        const impact = await orderService.getDeleteImpact(selectedOrder.id);
        if (isMounted) {
          setHasApprovedTimesheetsForSelectedOrder(impact.hasApprovedTimesheets);
        }
      } catch {
        if (isMounted) {
          setHasApprovedTimesheetsForSelectedOrder(false);
        }
      } finally {
        if (isMounted) {
          setLoadingDeleteEligibility(false);
        }
      }
    };

    loadDeleteEligibility();

    return () => {
      isMounted = false;
    };
  }, [selectedOrder]);

  const handleOpen = () => {
    setOpen(true);
    setFormData({
      ...initialFormData,
      orderId: selectedOrderId ? parseInt(selectedOrderId) : 0,
    });
    setEditingAssignment(null);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setEditingAssignment(null);
  };

  const handleEdit = (assignment: OrderAssignment) => {
    setEditingAssignment(assignment);
    setFormData({
      userId: assignment.userId,
      orderId: assignment.orderId,
      startDate: format(new Date(assignment.startDate), 'yyyy-MM-dd'),
      endDate: assignment.endDate ? format(new Date(assignment.endDate), 'yyyy-MM-dd') : '',
      costRate: assignment.costRate != null ? Number(assignment.costRate) : undefined,
      sellRate: assignment.sellRate != null ? Number(assignment.sellRate) : undefined,
    });
    setOpen(true);
  };

  const formatAssignmentEndDate = (endDate: string | null) => {
    if (!endDate) {
      return t('orderAssignments.ongoing', 'Ongoing');
    }

    return format(new Date(endDate), 'MMM d, yyyy');
  };

  const handleDelete = async (id: number) => {
    setDeleteTargetId(id);
  };

  const openOrderEditDialog = () => {
    if (!selectedOrder) return;

    setOrderEditData({
      code: selectedOrder.code,
      description: selectedOrder.description || '',
      startDate: selectedOrder.startDate ? format(new Date(selectedOrder.startDate), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      endDate: selectedOrder.endDate ? format(new Date(selectedOrder.endDate), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      plafond: selectedOrder.plafond != null ? formatPlafondForUi(Number(selectedOrder.plafond)) : '',
      clientId: selectedOrder.clientId,
      supplierId: selectedOrder.supplierId ?? null,
    });
    setOrderEditOpen(true);
  };

  const closeOrderEditDialog = () => {
    setOrderEditOpen(false);
    setOrderEditData(initialOrderEditData);
  };

  const handleSaveOrderEdit = async () => {
    if (!selectedOrder) return;

    try {
      setSavingOrderEdit(true);
      const parsedPlafond = parseItalianPlafond(orderEditData.plafond);

      await orderService.update(selectedOrder.id, {
        description: orderEditData.description,
        startDate: orderEditData.startDate,
        endDate: orderEditData.endDate,
        plafond: parsedPlafond,
        clientId: orderEditData.clientId,
        supplierId: orderEditData.supplierId,
      });

      showNotification(t('common.notifications.saveSuccess'), 'success');
      await fetchInitialData();
      closeOrderEditDialog();
    } catch (error) {
      if (error instanceof Error && error.message === 'invalid-plafond-format') {
        showNotification(t('pages.orders.errors.invalidPlafondFormat', 'Plafond must use a comma for cents and a dot only for thousands.'), 'error');
      } else {
        showNotification(
          getErrorMessage(error, t('pages.orders.errors.save', 'Failed to save order')),
          'error',
        );
      }
    } finally {
      setSavingOrderEdit(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteTargetId === null) return;

    try {
      setDeletingAssignmentId(deleteTargetId);
      await orderAssignmentService.delete(deleteTargetId);
      showNotification(t('common.notifications.operationSuccess'), 'success');
      setDeleteTargetId(null);

      // Refresh list
      if (selectedOrderId) {
        fetchOrderAssignments(parseInt(selectedOrderId));
      }
    } catch (error) {
      console.error('Failed to delete assignment:', error);
      showNotification(
        getErrorMessage(error, t('orderAssignments.errors.delete', 'Failed to delete')),
        'error',
      );
    } finally {
      setDeletingAssignmentId(null);
    }
  };

  const openDeleteOrderDialog = async () => {
    if (!selectedOrder) return;
    setDeleteOrderOpen(true);
    setDeleteImpact(null);

    try {
      setLoadingDeleteImpact(true);
      const impact = await orderService.getDeleteImpact(selectedOrder.id);
      setDeleteImpact(impact);
    } catch (error) {
      showNotification(
        getErrorMessage(error, t('pages.orders.errors.delete', 'Failed to delete order')),
        'error',
      );
      setDeleteOrderOpen(false);
    } finally {
      setLoadingDeleteImpact(false);
    }
  };

  const closeDeleteOrderDialog = () => {
    setDeleteOrderOpen(false);
    setDeleteImpact(null);
  };

  const handleConfirmDeleteOrder = async () => {
    if (!selectedOrder) return;
    if (deleteImpact?.hasApprovedTimesheets) return;

    try {
      setDeletingOrder(true);
      const shouldForceDelete = (deleteImpact?.totalTimesheets ?? 0) > 0;
      await orderService.delete(selectedOrder.id, { force: shouldForceDelete });
      showNotification(t('common.notifications.operationSuccess'), 'success');
      closeDeleteOrderDialog();
      navigate('/orders');
    } catch (error) {
      showNotification(
        getErrorMessage(error, t('pages.orders.errors.delete', 'Failed to delete order')),
        'error',
      );
    } finally {
      setDeletingOrder(false);
    }
  };

  const openSplitDialog = (assignment: OrderAssignment) => {
    const minSplitDate = new Date(assignment.startDate);
    minSplitDate.setDate(minSplitDate.getDate() + 1);

    setSplitTarget(assignment);
    setSplitForm({
      splitDate: format(minSplitDate, 'yyyy-MM-dd'),
      costRate: assignment.costRate != null ? String(assignment.costRate) : '',
      sellRate: assignment.sellRate != null ? String(assignment.sellRate) : '',
    });
    setSplitOpen(true);
  };

  const closeSplitDialog = () => {
    setSplitOpen(false);
    setSplitTarget(null);
    setSplitForm({
      splitDate: format(new Date(), 'yyyy-MM-dd'),
      costRate: '',
      sellRate: '',
    });
  };

  const parseRate = (value: string): number | undefined => {
    if (!value.trim()) {
      return undefined;
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed) || parsed < 0) {
      throw new Error(t('orderAssignments.errors.invalidRate', 'Invalid rate value'));
    }

    return parsed;
  };

  const handleSplitSubmit = async () => {
    if (!splitTarget) {
      return;
    }

    try {
      await orderAssignmentService.split(splitTarget.id, {
        splitDate: splitForm.splitDate,
        costRate: parseRate(splitForm.costRate),
        sellRate: parseRate(splitForm.sellRate),
      });

      showNotification(t('orderAssignments.split.success', 'Assignment split successfully'), 'success');
      closeSplitDialog();

      if (selectedOrderId) {
        fetchOrderAssignments(parseInt(selectedOrderId));
      }
    } catch (error) {
      showNotification(
        getErrorMessage(error, t('orderAssignments.split.error', 'Failed to split assignment')),
        'error',
      );
    }
  };

  const handleSubmit = async () => {
    if (!formData.userId || !formData.orderId) {
      showNotification(t('orderAssignments.errors.required', 'User and Order are required'), 'error');
      return;
    }

    const selectedOrderForValidation = orders.find((o) => o.id === formData.orderId);
    const orderStartStr = selectedOrderForValidation?.startDate;
    const orderEndStr = selectedOrderForValidation?.endDate;
    if (orderStartStr && orderEndStr) {
      const assignStart = new Date(formData.startDate);
      const orderStart = new Date(orderStartStr);
      const orderEnd = new Date(orderEndStr);

      if (assignStart < orderStart) {
        showNotification(
          t('orderAssignments.errors.startBeforeOrder', 'Assignment start date cannot be before the order start date'),
          'error',
        );
        return;
      }
      if (assignStart > orderEnd) {
        showNotification(
          t('orderAssignments.errors.startAfterOrder', 'Assignment start date cannot be after the order end date'),
          'error',
        );
        return;
      }
      if (formData.endDate) {
        const assignEnd = new Date(formData.endDate);
        if (assignEnd > orderEnd) {
          showNotification(
            t('orderAssignments.errors.endAfterOrder', 'Assignment end date cannot be after the order end date'),
            'error',
          );
          return;
        }
      }
    }

    setSaving(true);
    try {
      if (editingAssignment) {
        await orderAssignmentService.update(editingAssignment.id, {
          startDate: formData.startDate,
          endDate: formData.endDate || undefined,
          costRate: formData.costRate,
          sellRate: formData.sellRate,
        });
      } else {
        await orderAssignmentService.create({
          ...formData,
          endDate: formData.endDate || undefined,
        });
      }

      showNotification(t('common.notifications.saveSuccess'), 'success');
      handleClose();

      // Refresh list
      if (selectedOrderId) {
        fetchOrderAssignments(parseInt(selectedOrderId));
      }
    } catch (error) {
      console.error('Failed to save assignment:', error);
      showNotification(
        getErrorMessage(error, t('orderAssignments.errors.save', 'Failed to save')),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  const renderFormContent = () => {
    const selectableUsers = editingAssignment
      ? users
      : users.filter((user) => !assignments.some((assignment) => assignment.userId === user.id));

    return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label>{t('orderAssignments.form.user', 'User')}</Label>
        <Select
          value={String(formData.userId)}
          onValueChange={(value) => setFormData((prev) => ({ ...prev, userId: parseInt(value) }))}
          disabled={!!editingAssignment || selectableUsers.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('orderAssignments.form.selectUser', 'Select user...')} />
          </SelectTrigger>
          <SelectContent>
            {selectableUsers.length === 0 ? (
              <SelectItem value="__no_users__" disabled>
                {t('orderAssignments.form.alreadyAssigned', 'already assigned')}
              </SelectItem>
            ) : selectableUsers.map((user) => (
              <SelectItem key={user.id} value={String(user.id)}>
                {user.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>{t('orderAssignments.form.order', 'Order')}</Label>
        <div className="flex h-10 items-center rounded-md border bg-muted/50 px-3 text-sm text-muted-foreground">
          {selectedOrder ? `${selectedOrder.code} — ${selectedOrder.description}` : '—'}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t('orderAssignments.form.startDate', 'Start Date')}</Label>
          <Input
            type="date"
            value={formData.startDate}
            min={selectedOrder?.startDate as string | undefined}
            max={selectedOrder?.endDate as string | undefined}
            onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('orderAssignments.form.endDate', 'End Date')}</Label>
          <Input
            type="date"
            value={formData.endDate || ''}
            min={selectedOrder?.startDate as string | undefined}
            max={selectedOrder?.endDate as string | undefined}
            onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t('orderAssignments.form.costRate', 'Cost Rate')}</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={formData.costRate ?? ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                costRate: e.target.value === '' ? undefined : Number(e.target.value),
              }))
            }
            placeholder="0.00"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('orderAssignments.form.sellRate', 'Sell Rate')}</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={formData.sellRate ?? ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                sellRate: e.target.value === '' ? undefined : Number(e.target.value),
              }))
            }
            placeholder="0.00"
          />
        </div>
      </div>
    </div>
    );
  };

  const renderOrderTimeline = () => {
    const laneKey = 'userId';
    const grouped = visibleAssignments.reduce<Record<number, OrderAssignment[]>>((acc, assignment) => {
      const key = assignment[laneKey];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(assignment);
      return acc;
    }, {});

    const lanes = Object.entries(grouped).map(([groupId, lane]) => ({
      groupId,
      items: lane.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()),
    }));

    const dayMs = 24 * 60 * 60 * 1000;
    const toDay = (input: string | Date) => {
      const d = new Date(input);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };

    const selectedOrderStart = selectedOrder?.startDate ? toDay(selectedOrder.startDate) : null;
    const selectedOrderEnd = selectedOrder?.endDate ? toDay(selectedOrder.endDate) : null;

    const fallbackStart = lanes.flatMap((lane) => lane.items).reduce<Date | null>((min, item) => {
      const d = toDay(item.startDate);
      if (!min || d < min) {
        return d;
      }
      return min;
    }, null);

    const fallbackEnd = lanes.flatMap((lane) => lane.items).reduce<Date | null>((max, item) => {
      const itemEnd = item.endDate ? toDay(item.endDate) : toDay(new Date());
      if (!max || itemEnd > max) {
        return itemEnd;
      }
      return max;
    }, null);

    const rangeStart = selectedOrderStart ?? fallbackStart;
    const rangeEnd = selectedOrderEnd ?? fallbackEnd;

    if (!rangeStart || !rangeEnd || rangeEnd < rangeStart) {
      return null;
    }

    const totalDays = Math.max(1, Math.floor((rangeEnd.getTime() - rangeStart.getTime()) / dayMs) + 1);

    const monthTicks: Date[] = [];
    const tickDate = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
    while (tickDate <= rangeEnd) {
      monthTicks.push(new Date(tickDate));
      tickDate.setMonth(tickDate.getMonth() + 1);
    }
    const visibleMonths = getFilteredMonthTicks(monthTicks, isMobile);

    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="mb-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="inline-block h-3 w-8 rounded bg-emerald-500" />
                <span>{t('orderAssignments.timeline.activeSegment', 'In corso')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block h-3 w-8 rounded bg-slate-300 dark:bg-slate-700" />
                <span>{t('orderAssignments.timeline.inactiveSegment', 'Chiuso / In arrivo')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-full border border-foreground/50" />
                <span>{t('orderAssignments.timeline.cursors', 'Work period markers')}</span>
              </div>
            </div>

            <div className="relative h-10 overflow-hidden rounded border bg-muted/30">
              {monthTicks.map((tick) => {
                const offsetDays = Math.floor((tick.getTime() - rangeStart.getTime()) / dayMs);
                const leftPct = Math.max(0, Math.min(100, (offsetDays / totalDays) * 100));
                const key = `${tick.getFullYear()}-${tick.getMonth()}`;
                const isVisible = visibleMonths.has(key);

                return (
                  <div
                    key={key}
                    className="absolute top-0 h-full border-l border-muted-foreground/25"
                    style={{ left: `${leftPct}%` }}
                  >
                    {isVisible && (
                      <span className="absolute -top-0.5 ml-1 text-[10px] font-medium text-muted-foreground">
                        {format(tick, 'MMM yy')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {lanes.map(({ groupId, items }) => {
          const first = items[0];

          return (
            <Card key={`${laneKey}-${groupId}`}>
              <CardContent className="p-4">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <>
                      <p className="font-semibold">{first.user?.name || t('orderAssignments.columns.user', 'User')}</p>
                      <p className="text-sm text-muted-foreground">{first.user?.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('orderAssignments.summary.totalHours', 'Hours worked')}: {formatWorkedHoursForUser(first.userId)}
                      </p>
                    </>
                  </div>
                </div>

                <div className="relative h-14 rounded border bg-muted/20">
                  {monthTicks.map((tick) => {
                    const offsetDays = Math.floor((tick.getTime() - rangeStart.getTime()) / dayMs);
                    const leftPct = Math.max(0, Math.min(100, (offsetDays / totalDays) * 100));
                    const key = `${tick.getFullYear()}-${tick.getMonth()}`;
                    
                    return (
                      <div
                        key={`${laneKey}-${groupId}-${key}`}
                        className="absolute top-0 h-full border-l border-muted-foreground/20"
                        style={{ left: `${leftPct}%` }}
                      />
                    );
                  })}

                  {items.map((assignment, idx) => {
                    const segmentStart = toDay(assignment.startDate);
                    const segmentEnd = assignment.endDate ? toDay(assignment.endDate) : rangeEnd;
                    const clampedStart = segmentStart < rangeStart ? rangeStart : segmentStart;
                    const clampedEnd = segmentEnd > rangeEnd ? rangeEnd : segmentEnd;
                    const offsetStartDays = Math.floor((clampedStart.getTime() - rangeStart.getTime()) / dayMs);
                    const segmentDays = Math.max(1, Math.floor((clampedEnd.getTime() - clampedStart.getTime()) / dayMs) + 1);
                    const leftPct = (offsetStartDays / totalDays) * 100;
                    const widthPct = (segmentDays / totalDays) * 100;
                    const activeSegment = isActive(assignment);
                    const colorClass = activeSegment ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700';

                    return (
                      <div
                        key={assignment.id}
                        className={`absolute top-1/2 h-6 -translate-y-1/2 rounded ${colorClass} shadow-sm`}
                        style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 1.5)}%` }}
                        title={`${assignment.order?.code || t('orderAssignments.columns.order', 'Order')} | ${format(segmentStart, 'dd/MM/yyyy')} - ${assignment.endDate ? format(segmentEnd, 'dd/MM/yyyy') : t('orderAssignments.ongoing', 'Ongoing')}`}
                      >
                        <span className="absolute -left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border border-white bg-background" />
                        <span className="absolute -right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border border-white bg-background" />
                      </div>
                    );
                  })}
                </div>

                <div className="mt-3 space-y-2">
                  {items.map((assignment, idx) => {
                    const margin =
                      assignment.costRate != null && assignment.sellRate != null
                        ? assignment.sellRate - assignment.costRate
                        : null;
                    const activeSegment = isActive(assignment);
                    const segmentDotClass = activeSegment ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700';
                    const segmentLabel = assignment.user?.name || t('orderAssignments.columns.user', 'User');

                    return (
                      <div key={`meta-${assignment.id}`} className="rounded border p-2 text-sm">
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 font-medium">
                              <span className={`inline-block h-2.5 w-2.5 rounded-full ${segmentDotClass}`} />
                              <span>{segmentLabel}</span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {format(new Date(assignment.startDate), 'dd/MM/yyyy')} - {assignment.endDate ? format(new Date(assignment.endDate), 'dd/MM/yyyy') : t('orderAssignments.ongoing', 'Ongoing')}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              title={t('orderAssignments.split.action', 'Split from date')}
                              onClick={() => openSplitDialog(assignment)}
                            >
                              <Split className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(assignment)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(assignment.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-2 text-xs text-muted-foreground md:grid-cols-3">
                          <div className="flex items-center gap-2">
                            <Euro className="h-3.5 w-3.5" />
                            <span>{t('orderAssignments.form.costRate', 'Cost Rate')}: {assignment.costRate ?? '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Euro className="h-3.5 w-3.5" />
                            <span>{t('orderAssignments.form.sellRate', 'Sell Rate')}: {assignment.sellRate ?? '-'}</span>
                          </div>
                          <div>
                            {t('orderAssignments.margin', 'Margin')}: {margin != null ? margin.toFixed(2) : '-'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  const parseLocalDate = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const getToday = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };

  const isActive = (assignment: OrderAssignment) => {
    const today = getToday();
    const start = parseLocalDate(assignment.startDate);
    const end = assignment.endDate ? parseLocalDate(assignment.endDate) : null;
    return start <= today && (!end || end >= today);
  };

  const isUpcoming = (assignment: OrderAssignment) => {
    return parseLocalDate(assignment.startDate) > getToday();
  };

  const isPast = (assignment: OrderAssignment) => {
    if (!assignment.endDate) return false;
    return parseLocalDate(assignment.endDate) < getToday();
  };

  const sortAssignmentsByStartDateDesc = (group: OrderAssignment[]) => {
    return [...group].sort(
      (a: OrderAssignment, b: OrderAssignment) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  };

  const groupKey = 'userId';
  const visibleAssignments = hideClosedGroups
    ? Array.from(
        assignments.reduce<Map<number, OrderAssignment[]>>((acc, assignment) => {
          const key = assignment[groupKey as 'orderId' | 'userId'] as number;
          if (!acc.has(key)) {
            acc.set(key, []);
          }
          acc.get(key)!.push(assignment);
          return acc;
        }, new Map<number, OrderAssignment[]>()).values()
      )
        .filter((group) => group.some((a) => isActive(a) || isUpcoming(a)))
        .flat()
    : assignments;

  const renderGroupHeaderRow = (assignment: OrderAssignment, key: number) => (
    <TableRow key={`group-${key}`} className="bg-muted/30 hover:bg-muted/30">
      <TableCell colSpan={7} className="py-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-background shadow-sm">
              <User className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium leading-none">
                {assignment.user?.name || t('orderAssignments.columns.user', 'User')}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {assignment.user?.role ? getRoleLabel(assignment.user.role, t) : '-'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('orderAssignments.summary.totalHours', 'Hours worked')}: {formatWorkedHoursForUser(assignment.userId)}
              </p>
            </div>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );

  const renderAssignmentRow = (assignment: OrderAssignment, nested = false) => (
    <TableRow key={assignment.id} className={isPast(assignment) ? 'opacity-50' : ''}>
      <TableCell className={nested ? 'w-[320px] pl-8' : 'font-medium'}>
        <div className="leading-tight">
          <p className={nested ? 'text-muted-foreground' : 'font-medium'}>{nested ? '↳' : assignment.user?.name}</p>
          {!nested && (
            <p className="mt-1 text-xs text-muted-foreground">{assignment.user?.role ? getRoleLabel(assignment.user.role, t) : '-'}</p>
          )}
        </div>
      </TableCell>
      <TableCell>{format(new Date(assignment.startDate), 'MMM d, yyyy')}</TableCell>
      <TableCell>{formatAssignmentEndDate(assignment.endDate)}</TableCell>
      <TableCell>
        {isActive(assignment) ? (
          <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-0 text-xs">
            {t('orderAssignments.status.active', 'Active')}
          </Badge>
        ) : isUpcoming(assignment) ? (
          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0 text-xs">
            {t('orderAssignments.status.upcoming', 'Upcoming')}
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">
            {t('orderAssignments.status.expired', 'Expired')}
          </Badge>
        )}
      </TableCell>
      <TableCell className="font-mono text-sm">{assignment.sellRate != null ? `€${Number(assignment.sellRate).toFixed(2)}` : '-'}</TableCell>
      <TableCell className="font-mono text-sm">{assignment.costRate != null ? `€${Number(assignment.costRate).toFixed(2)}` : '-'}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            title={t('orderAssignments.split.action', 'Split from date')}
            onClick={() => openSplitDialog(assignment)}
          >
            <Split className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => handleEdit(assignment)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive"
            onClick={() => handleDelete(assignment.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  const renderGroupedTableBody = () => {
    const grouped = new Map<number, OrderAssignment[]>();
    for (const a of visibleAssignments) {
      const key = a[groupKey as 'orderId' | 'userId'] as number;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(a);
    }
    const rows: React.ReactNode[] = [];
    Array.from(grouped.entries()).forEach(([key, group]) => {
      const sortedGroup = sortAssignmentsByStartDateDesc(group);
      const active = sortedGroup.filter((a: OrderAssignment) => isActive(a));
      const upcoming = sortedGroup.filter((a: OrderAssignment) => isUpcoming(a));
      const past = sortedGroup.filter((a: OrderAssignment) => isPast(a));
      rows.push(renderGroupHeaderRow(sortedGroup[0], key));
      active.forEach((a: OrderAssignment) => rows.push(renderAssignmentRow(a, true)));
      upcoming.forEach((a: OrderAssignment) => rows.push(renderAssignmentRow(a, true)));
      if (past.length > 0) {
        const isExpanded = expandedGroups.has(key);
        rows.push(
          <TableRow
            key={`expand-${key}`}
            className="cursor-pointer hover:bg-muted/30"
            onClick={() => toggleGroup(key)}
          >
            <TableCell colSpan={7} className="py-1.5">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                {t('orderAssignments.expiredSegments', '{{count}} expired', { count: past.length })}
              </div>
            </TableCell>
          </TableRow>
        );
        if (isExpanded) {
          past.forEach((a: OrderAssignment) => rows.push(renderAssignmentRow(a, true)));
        }
      }
      rows.push(
        <TableRow key={`separator-${key}`} className="pointer-events-none">
          <TableCell colSpan={7} className="h-2 p-0" />
        </TableRow>
      );
    });
    return rows;
  };

  const renderGroupHeaderCard = (
    assignment: OrderAssignment,
    key: number,
    isExpanded: boolean,
    periodsCount: number,
  ) => (
    <div key={`group-mobile-${key}`} className="mb-2 rounded-lg border bg-muted/30 px-4 py-3">
      <button
        type="button"
        onClick={() => toggleUserGroup(key)}
        className="flex w-full items-center gap-3 text-left"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-background shadow-sm">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium leading-none">
              {assignment.user?.name || t('orderAssignments.columns.user', 'User')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {assignment.user?.role ? getRoleLabel(assignment.user.role, t) : '-'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {periodsCount} periodi
            </p>
          </div>
        </div>
      </button>
    </div>
  );

  const renderMobileCard = (assignment: OrderAssignment, nested = false) => (
    <Card key={assignment.id} className="mb-2">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            {!nested && (
              <>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{assignment.user?.name}</span>
                </div>
                <Badge variant="outline" className="text-xs">
                  {assignment.user?.role ? getRoleLabel(assignment.user.role, t) : '-'}
                </Badge>
              </>
            )}
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
              <Calendar className="h-3 w-3" />
              <span>
                {format(new Date(assignment.startDate), 'MMM d, yyyy')} -{' '}
                {formatAssignmentEndDate(assignment.endDate)}
              </span>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {t('orderAssignments.summary.totalHours', 'Hours worked')}: {formatWorkedHoursForAssignment(assignment.id)}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {t('orderAssignments.form.costRate', 'Cost Rate')}: {assignment.costRate ?? '-'} | {t('orderAssignments.form.sellRate', 'Sell Rate')}: {assignment.sellRate ?? '-'}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              title={t('orderAssignments.split.action', 'Split from date')}
              onClick={() => openSplitDialog(assignment)}
            >
              <Split className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => handleEdit(assignment)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive"
              onClick={() => handleDelete(assignment.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderGroupedMobile = () => {
    const grouped = new Map<number, OrderAssignment[]>();
    for (const a of visibleAssignments) {
      const key = a[groupKey as 'orderId' | 'userId'] as number;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(a);
    }
    const cards: React.ReactNode[] = [];
    Array.from(grouped.entries()).forEach(([key, group]) => {
      const sortedGroup = sortAssignmentsByStartDateDesc(group);
      const isExpanded = expandedUserGroups.has(key);
      cards.push(renderGroupHeaderCard(sortedGroup[0], key, isExpanded, sortedGroup.length));

      if (isExpanded) {
        sortedGroup.forEach((a: OrderAssignment) => cards.push(renderMobileCard(a, true)));
      }
    });
    return cards;
  };

  return (
    <PageTransition>
      <div className="p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900">
              <Users className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold">{selectedOrder ? selectedOrder.code : t('orderAssignments.title', 'Order Assignments')}</h1>
              <p className="text-sm text-muted-foreground">
                {selectedOrder
                  ? selectedOrder.description
                  : t('orderAssignments.subtitle', 'Manage user-order assignments')}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <div className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 sm:justify-start">
              <Label htmlFor="hide-closed-groups" className="text-sm font-normal">
                {t('orderAssignments.filters.hideClosedGroups', 'Hide expired groups')}
              </Label>
              <Switch
                id="hide-closed-groups"
                checked={hideClosedGroups}
                onCheckedChange={setHideClosedGroups}
              />
            </div>
            <Button
              variant="outline"
              onClick={toggleViewMode}
              className="w-full gap-2 sm:w-auto"
            >
              {viewMode === 'table' ? (
                <><GanttChartSquare className="h-4 w-4" />{t('orderAssignments.viewMode.timelineLabel', 'Vista timeline')}</>
              ) : (
                <><LayoutList className="h-4 w-4" />{t('orderAssignments.viewMode.tableLabel', 'Vista tabella')}</>
              )}
            </Button>
            <Button onClick={handleOpen} className="w-full bg-orange-500 hover:bg-orange-600 sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              {t('orderAssignments.actions.create', 'Add Assignment')}
            </Button>
          </div>
        </div>

        {selectedOrder && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Button variant="ghost" size="sm" onClick={() => navigate('/orders')} className="gap-1">
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('menu.orders', 'Orders')}
            </Button>
            <span>/</span>
            <span className="font-medium text-foreground">{t('orderAssignments.detailTitle', 'Order detail')}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={openOrderEditDialog}
              className="ml-auto gap-1"
            >
              <Pencil className="h-3.5 w-3.5" />
              {t('pages.orders.actions.edit', 'Edit Order')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={openDeleteOrderDialog}
              className="gap-1"
              disabled={loadingDeleteEligibility}
              hidden={loadingDeleteEligibility || hasApprovedTimesheetsForSelectedOrder}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('pages.orders.actions.delete', 'Delete Order')}
            </Button>
          </div>
        )}

        {/* Order KPI Summary */}
        {selectedOrder && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Users className="h-4 w-4" />
                  <span className="text-xs">{t('orderAssignments.summary.activeUsers', 'Team attivo')}</span>
                </div>
                <p className="text-2xl font-semibold">
                  {orderSummary != null ? orderSummary.activeUsers : '—'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Clock className="h-4 w-4" />
                  <span className="text-xs">{t('orderAssignments.summary.totalHours', 'Ore lavorate')}</span>
                </div>
                <p className="text-2xl font-semibold">
                  {orderSummary != null ? `${orderSummary.totalHours}h` : '—'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-xs">{t('orderAssignments.summary.billingRevenue', 'Ricavo stimato')}</span>
                </div>
                <p className="text-2xl font-semibold">
                  {orderSummary?.billingRevenue != null
                    ? `€${orderSummary.billingRevenue.toFixed(0)}`
                    : '—'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <TrendingDown className="h-4 w-4" />
                  <span className="text-xs">{t('orderAssignments.summary.netProfit', 'Margine netto')}</span>
                </div>
                <p className={`text-2xl font-semibold ${
                  orderSummary?.netProfit != null && orderSummary.netProfit >= 0
                    ? 'text-emerald-600'
                    : orderSummary?.netProfit != null
                    ? 'text-red-500'
                    : ''
                }`}>
                  {orderSummary?.netProfit != null
                    ? `€${orderSummary.netProfit.toFixed(0)}`
                    : '—'}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {!selectedOrder && !loading && (
          <Card>
            <CardContent className="p-8 text-center space-y-3">
              <ShoppingCart className="h-10 w-10 mx-auto text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">{t('orderAssignments.selectOrder', 'Select an order...')}</p>
              <Button variant="outline" onClick={() => navigate('/orders')}>
                {t('menu.orders', 'Orders')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Assignments */}
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : assignments.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">
                {t('orderAssignments.empty.title', 'No Assignments')}
              </h3>
              <p className="text-muted-foreground mb-4">
                {t('orderAssignments.empty.byOrder', 'This order has no user assignments.')}
              </p>
              <Button onClick={handleOpen} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                {t('orderAssignments.actions.create', 'Add Assignment')}
              </Button>
            </CardContent>
          </Card>
        ) : visibleAssignments.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">
                {t('orderAssignments.empty.filteredTitle', 'No open groups')}
              </h3>
              <p className="text-muted-foreground">
                {t('orderAssignments.empty.filteredDescription', 'The current filter hides groups with only closed work periods.')}
              </p>
            </CardContent>
          </Card>
        ) : viewMode === 'gantt' ? (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t('orderAssignments.timelineTitle', 'Gantt Timeline')}
            </h3>
            {renderOrderTimeline()}
          </div>
        ) : (
          <div>{renderGroupedMobile()}</div>
        )}

        {/* Form Dialog/BottomSheet */}
        <BottomSheet
          open={open}
          onClose={handleClose}
          title={
            editingAssignment
              ? t('orderAssignments.actions.edit', 'Edit Assignment')
              : t('orderAssignments.actions.create', 'Add Assignment')
          }
        >
          {renderFormContent()}
          <div className="sticky bottom-0 -mx-4 mt-4 flex justify-end gap-2 border-t bg-background px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3">
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.save', 'Save')}
            </Button>
          </div>
        </BottomSheet>

        <BottomSheet
          open={splitOpen}
          onClose={closeSplitDialog}
          title={t('orderAssignments.split.dialogTitle', 'Split Assignment')}
          description={t(
            'orderAssignments.split.dialogDescription',
            'Create a new segment from a specific date with optional updated rates',
          )}
        >
          <div className="space-y-4 py-4">
            {splitTarget && (
              <div className="rounded border bg-muted/20 p-3 text-xs text-muted-foreground">
                <p>
                  {splitTarget.user?.name} - {splitTarget.order?.code}
                </p>
                <p>
                  {format(new Date(splitTarget.startDate), 'dd/MM/yyyy')} - {splitTarget.endDate ? format(new Date(splitTarget.endDate), 'dd/MM/yyyy') : t('orderAssignments.ongoing', 'Ongoing')}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('orderAssignments.split.date', 'Split Date')}</Label>
              <Input
                type="date"
                value={splitForm.splitDate}
                onChange={(e) =>
                  setSplitForm((prev) => ({
                    ...prev,
                    splitDate: e.target.value,
                  }))
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('orderAssignments.form.costRate', 'Cost Rate')}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={splitForm.costRate}
                  onChange={(e) =>
                    setSplitForm((prev) => ({
                      ...prev,
                      costRate: e.target.value,
                    }))
                  }
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('orderAssignments.form.sellRate', 'Sell Rate')}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={splitForm.sellRate}
                  onChange={(e) =>
                    setSplitForm((prev) => ({
                      ...prev,
                      sellRate: e.target.value,
                    }))
                  }
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 -mx-4 mt-4 flex justify-end gap-2 border-t bg-background px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3">
            <Button variant="outline" onClick={closeSplitDialog}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={handleSplitSubmit}>
              {t('orderAssignments.split.action', 'Split from date')}
            </Button>
          </div>
        </BottomSheet>

        <BottomSheet
          open={deleteTargetId !== null}
          onClose={() => setDeleteTargetId(null)}
          title={t('orderAssignments.confirmDeleteTitle', 'Delete assignment')}
        >
          <p className="mb-6 text-sm text-muted-foreground">
            {t('orderAssignments.confirmDelete', 'Delete this assignment?')}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteTargetId(null)} disabled={deletingAssignmentId !== null}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={deletingAssignmentId !== null}>
              {deletingAssignmentId !== null ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('common.delete', 'Delete')}
            </Button>
          </div>
        </BottomSheet>

        <BottomSheet
          open={deleteOrderOpen}
          onClose={closeDeleteOrderDialog}
          title={t('pages.orders.confirmDeleteTitle', 'Delete order')}
        >
          {loadingDeleteImpact ? (
            <div className="mb-6 flex items-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('common.loading', 'Loading...')}
            </div>
          ) : deleteImpact?.hasApprovedTimesheets ? (
            <div className="mb-6 space-y-2">
              <p className="text-sm text-destructive">
                {t('pages.orders.deleteDialog.approvedExistsTitle', 'Failed to delete order: approved timesheets exist.')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('pages.orders.deleteDialog.approvedExistsDescription', 'Delete is blocked because this order contains approved timesheets.')}
              </p>
            </div>
          ) : (
            <div className="mb-6 space-y-3">
              <p className="text-sm text-muted-foreground">
                {(deleteImpact?.totalTimesheets ?? 0) > 0
                  ? t('pages.orders.deleteDialog.forceDeleteDescription', 'Timesheets found for this order. Deletion will remove non-approved timesheets and assignments.')
                  : t('pages.orders.confirmDelete', 'Are you sure you want to delete this order?')}
              </p>
              {(deleteImpact?.usersWithTimesheets.length ?? 0) > 0 && (
                <div className="rounded-md border p-3">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('pages.orders.deleteDialog.usersWithTimesheets', 'Users with timesheets')}
                  </p>
                  <ul className="space-y-1 text-sm">
                    {deleteImpact?.usersWithTimesheets.map((entry) => (
                      <li key={entry.userId} className="flex items-center justify-between gap-2">
                        <span className="truncate">{entry.userName}</span>
                        <span className="text-xs text-muted-foreground">{entry.total}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeDeleteOrderDialog} disabled={deletingOrder}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteOrder}
              disabled={deletingOrder || loadingDeleteImpact || Boolean(deleteImpact?.hasApprovedTimesheets)}
            >
              {deletingOrder ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {(deleteImpact?.totalTimesheets ?? 0) > 0 ? t('pages.orders.deleteDialog.forceDeleteAction', 'Force delete and clean') : t('common.delete', 'Delete')}
            </Button>
          </div>
        </BottomSheet>

        <BottomSheet
          open={orderEditOpen}
          onClose={closeOrderEditDialog}
          title={t('pages.orders.actions.edit', 'Edit Order')}
          description={t('pages.orders.editDescription', 'Update order details')}
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('pages.orders.columns.code', 'Order Code')}</Label>
              <Input value={orderEditData.code} disabled />
            </div>

            <div className="space-y-2">
              <Label>{t('pages.orders.columns.description', 'Description')}</Label>
              <textarea
                className="min-h-[96px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={orderEditData.description}
                onChange={(e) => setOrderEditData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={t('pages.orders.descriptionPlaceholder', 'Order description')}
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.orders.columns.startDate', 'Start Date')}</Label>
                <Input
                  type="date"
                  value={orderEditData.startDate}
                  onChange={(e) => setOrderEditData((prev) => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.orders.columns.endDate', 'End Date')}</Label>
                <Input
                  type="date"
                  value={orderEditData.endDate}
                  onChange={(e) => setOrderEditData((prev) => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('pages.orders.columns.plafond', 'Plafond')}</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={orderEditData.plafond}
                onChange={(e) => setOrderEditData((prev) => ({ ...prev, plafond: e.target.value }))}
                placeholder="0,00"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('pages.orders.columns.client', 'Client')}</Label>
              <Select
                value={orderEditData.clientId ? String(orderEditData.clientId) : ''}
                onValueChange={(value) => setOrderEditData((prev) => ({ ...prev, clientId: Number(value) }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('pages.orders.selectClient', 'Select client...')} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={String(client.id)}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('pages.orders.columns.suppliers', 'Suppliers')}</Label>
              <Select
                value={orderEditData.supplierId ? String(orderEditData.supplierId) : '__none__'}
                onValueChange={(value) =>
                  setOrderEditData((prev) => ({
                    ...prev,
                    supplierId: value === '__none__' ? null : Number(value),
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('pages.orders.selectSupplier', 'Select supplier...')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('common.none', 'None')}</SelectItem>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={String(supplier.id)}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-3">
              <Button variant="outline" onClick={closeOrderEditDialog} disabled={savingOrderEdit} className="flex-1">
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleSaveOrderEdit} disabled={savingOrderEdit || !orderEditData.clientId} className="flex-1 bg-orange-500 hover:bg-orange-600">
                {savingOrderEdit ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.saving', 'Saving...')}
                  </>
                ) : (
                  t('common.save', 'Save')
                )}
              </Button>
            </div>
          </div>
        </BottomSheet>
      </div>
    </PageTransition>
  );
};

export default OrderAssignmentManager;
